import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Card, Color, SemanticColor } from '@bb/elements-web';
import { useSupraAccessDetailsPageContext } from '../SupraAccessDetailsPageContext';
import ChangePinModal from './ChangePinModal';

const MyKeyCard: React.FC = () => {
  const { subscription, pin } = useSupraAccessDetailsPageContext();

  const [pinVisible, setPinVisible] = useState(false);
  const [editPinOpen, setEditPinOpen] = useState(false);

  const serialNumber = subscription?.keySerialNumber;
  const hasPin = Boolean(pin);

  // Key serial and PIN are required to render the content of this card
  // If missing, we'll still render the card but show an error message
  const shouldRenderDetails = Boolean(serialNumber && hasPin);

  return (
    <>
      <StyledCard title="My Key" dataCy="my-key-card">
        {shouldRenderDetails ? (
          <>
            <Section>
              <Label>Serial number</Label>
              <Value>{serialNumber}</Value>
            </Section>
            <PinRow>
              <div>
                <Label>PIN</Label>
                <PinValueRow>
                  <PinValue>{pinVisible ? pin : '••••'}</PinValue>
                  <ToggleLink
                    type="button"
                    onClick={() => setPinVisible(prev => !prev)}
                    aria-label={
                      pinVisible ? 'Hide PIN code' : 'Reveal PIN code'
                    }
                    aria-live="polite"
                  >
                    {pinVisible ? 'Hide' : 'Reveal'}
                  </ToggleLink>
                </PinValueRow>
              </div>
              <Button
                minWidth="auto"
                size="middle"
                type="secondary"
                onClick={() => setEditPinOpen(true)}
                dataCy="my-key-edit-pin"
              >
                Edit
              </Button>
            </PinRow>
          </>
        ) : (
          <EmptyMessage data-cy="my-key-missing-serial-pin">
            Missing Key Information!
          </EmptyMessage>
        )}
      </StyledCard>
      <ChangePinModal
        visible={editPinOpen}
        existingPin={pin}
        onClose={() => setEditPinOpen(false)}
      />
    </>
  );
};

export default MyKeyCard;

const StyledCard = styled(Card)`
  .ant-card-head-title {
    font-size: 20px;
    font-weight: 600;
    color: ${Color.PRIMARY_LIGHT_TEXT};
  }
  .ant-card-head {
    border-bottom: none;
    padding-bottom: 0;
    min-height: auto;
  }
  .ant-card-body {
    padding-top: 8px;
  }
`;

const Section = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: ${Color.BODY_TEXT};
  margin-bottom: 4px;
`;

const Value = styled.div`
  font-size: 16px;
  color: ${Color.PRIMARY_LIGHT_TEXT};
`;

const PinValue = styled(Value)`
  font-family: SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace;
`;

const PinRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
`;

const PinValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ToggleLink = styled.button`
  background: none;
  border: 0;
  padding: 0;
  color: ${Color.PRIMARY};
  font-size: 16px;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const EmptyMessage = styled.div`
  font-size: 20px;
  color: ${SemanticColor.danger};
  padding: 8px 0;
`;
